// src/Advance.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import baseURL from './baseUrl';
import logo from './logo.new1.png';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Advance = () => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [receiptData, setReceiptData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cashAmount: '',
    pureWeight: '',
    type: 'given',
    advanceType: 'cash'
  });

  // Add a custom setter with logging
  const setCustomFormData = (updates) => {
    console.error('CRITICAL DEBUG - Updating form data:', JSON.stringify(updates, null, 2));
    
    // Intercept and correct any unexpected type
    if (updates.type === 'received') {
      console.error('CRITICAL DEBUG - Attempted to set invalid type "received"');
      updates.type = 'taken';
    }

    setFormData(prevData => {
      const newData = { ...prevData, ...updates };
      console.error('CRITICAL DEBUG - New form data:', JSON.stringify(newData, null, 2));
      return newData;
    });
  };

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientNotFound, setClientNotFound] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    let timer;
    if (showSuccessPopup) {
      timer = setTimeout(() => {
        setShowSuccessPopup(false);
        setMessage(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessPopup]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (err) {
      setError('Error fetching clients');
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCustomFormData({ name: searchValue });

    if (searchValue.trim() === '') {
      setFilteredClients(clients);
      setClientNotFound(false);
      return;
    }

    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(searchValue.toLowerCase())
    );

    setFilteredClients(filtered);
    setClientNotFound(filtered.length === 0);
  };

  const handleClientSelect = (clientName) => {
    setCustomFormData({ name: clientName });
    setSearchTerm(clientName);
    setFilteredClients([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      handleSearch(value);
    } else if (name === 'pureWeight') {
      // Allow entering digits, including decimal point
      // Limit to 3 decimal places
      const regex = /^\d*\.?\d{0,3}$/;
      if (regex.test(value)) {
        setCustomFormData({ [name]: value });
      }
    } else if (name === 'cashAmount') {
      // Allow entering digits, including decimal point
      // Limit to 2 decimal places
      const regex = /^\d*\.?\d{0,2}$/;
      if (regex.test(value) || value === '') {
        setCustomFormData({ [name]: value });
      }
    } else {
      setCustomFormData({ [name]: value });
    }
  };

  const resetForm = () => {
    setCustomFormData({
      name: '',
      cashAmount: '',
      pureWeight: '',
      type: 'given',
      advanceType: 'cash'
    });
    setSearchTerm('');
    setMessage(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setShowSuccessPopup(false);
    setReceiptData(null); // Reset receipt data

    // Comprehensive logging of form state before submission
    console.error('PRE-SUBMISSION FORM DATA:', JSON.stringify({
      name: formData.name,
      type: formData.type,
      advanceType: formData.advanceType,
      cashAmount: formData.cashAmount,
      pureWeight: formData.pureWeight
    }, null, 2));

    // Explicitly prevent 'received' from being used
    if (formData.type === 'received') {
      console.error('CRITICAL: Attempted to submit with "received" type');
      setError('Invalid advance type');
      return;
    }

    // Explicitly map to correct type
    const submitType = formData.type === 'taken' ? 'taken' : 'given';

    try {
      let endpoint;
      let payload;

      if (formData.advanceType === 'cash') {
        endpoint = `${baseURL}/api/transactions/cash-advance`;
        payload = {
          name: formData.name,
          cashAmount: parseFloat(formData.cashAmount),
          type: submitType
        };
      } else {
        endpoint = `${baseURL}/api/transactions/pure-weight-advance`;
        payload = {
          name: formData.name,
          pureWeight: parseFloat(formData.pureWeight),
          type: submitType
        };
      }

      // Final payload logging
      console.error('SUBMISSION PAYLOAD:', JSON.stringify(payload, null, 2));

            const response = await axios.post(endpoint, payload);

            // Generate receipt data
            setReceiptData({
            name: formData.name,
            type: submitType,
            advanceType: formData.advanceType,
            cashAmount: formData.cashAmount,
            pureWeight: formData.pureWeight,
            timestamp: new Date().toISOString()
            });

            setMessage(response.data.message || `${formData.advanceType === 'cash' ? 'Cash' : 'Pure Weight'} advance created successfully for ${formData.name}`);
            setShowSuccessPopup(true);
            resetForm(); // Clear form immediately

    } catch (error) {
      console.error('SUBMISSION ERROR:', JSON.stringify(error, null, 2));
      setError(error.response?.data?.message || 'An error occurred while creating the advance.');
    }
  };

  const handlePrintReceipt = () => {
    // Calculate number of items (Advance has 1 row, but we'll use scaling for consistency)
    let itemCount = 1;

    // Calculate scale factor based on item count
    let scaleFactor = 1;
    if (itemCount <= 1) {
      scaleFactor = 1.0;
    } else if (itemCount === 2) {
      scaleFactor = 0.98;
    } else if (itemCount === 3) {
      scaleFactor = 0.92;
    } else if (itemCount === 4) {
      scaleFactor = 0.88;
    } else if (itemCount >= 5) {
      scaleFactor = 0.85;
    }

    // Calculate scaled values
    const baseFontSize = 14;
    const scaledFontSize = Math.max(13, baseFontSize * scaleFactor);
    const scaledHeaderFont = Math.max(11, 12 * scaleFactor);
    const scaledMetaFont = Math.max(12, 13 * scaleFactor);
    const scaledReceiptNoFont = Math.max(17, 19 * scaleFactor);
    const scaledNameFont = Math.max(19, 21 * scaleFactor);
    const scaledInfoFont = Math.max(12, 14 * scaleFactor);
    const scaledFooterFont = Math.max(10, 11 * scaleFactor);
    const scaledDateFont = Math.max(12, 13 * scaleFactor);
    
    const scaledPadding = Math.max(6, 8 * scaleFactor);
    const scaledContainerPadding = Math.max(15, 20 * scaleFactor);
    const scaledMargin = Math.max(6, 8 * scaleFactor);
    const scaledLogoWidth = Math.max(100, 120 * scaleFactor);
    const scaledGap = Math.max(8, 12 * scaleFactor);
    const scaledRowGap = Math.max(1.5, 2 * scaleFactor);
    const scaledColumnGap = Math.max(5, 6 * scaleFactor);

    const printWindow = document.createElement('iframe');
    document.body.appendChild(printWindow);

    const doc = printWindow.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Print Advance Receipt</title>');
    doc.write('<style>');
    doc.write('body { font-family: Arial, sans-serif; margin: 0; padding: ' + scaledContainerPadding + 'px; background-color: #f9f9f9; }');
    doc.write('@page { margin: 0; size: A4; }');
    doc.write('.container { width: 80%; max-width: 100%; margin: auto; padding: ' + scaledContainerPadding + 'px; background: #fff; border: 1px solid #ccc; border-radius: 8px; page-break-inside: avoid; }');
    doc.write('h1 { text-align: center; font-size: ' + scaledHeaderFont + 'px; margin-bottom: 0px; margin-top: -10px; }');
    doc.write('.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: ' + (scaledMargin * 1.5) + 'px; }');
    doc.write('.logo { width: ' + scaledLogoWidth + 'px; height: auto; object-fit: contain; filter:contrast(220%); }');
    doc.write('.table { width: 100%; border-collapse: collapse; margin-top: ' + scaledMargin + 'px; font-size: ' + scaledFontSize + 'px; page-break-inside: avoid; }');
    doc.write('.table th, .table td { border: 1px solid #000; padding: ' + scaledPadding + 'px; text-align: center; }');
    doc.write('.table th { background-color: #f2f2f2; font-weight: bold; }');
    doc.write('.table tbody tr { page-break-inside: avoid; }');
    doc.write('.totals { margin-top: ' + scaledMargin + 'px; width: 100%; border-collapse: collapse; page-break-inside: avoid; }');
    doc.write('.total-row { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; font-size: ' + scaledFontSize + 'px; page-break-inside: avoid; }');
    doc.write('.total-row > span { padding: ' + scaledPadding + 'px; border-right: 1px solid #000; }');
    doc.write('.total-row > span:first-child { font-weight: bold; text-align: right; background: #f2f2f2; }');
    doc.write('.total-row > span:last-child { text-align: right; border-right: none; }');
    doc.write('.total-row:first-child { border-bottom: none; }');
    doc.write('.meta-row { display: grid; grid-template-columns: 1fr 1fr; gap: ' + scaledGap + 'px; margin: ' + (scaledMargin * 0.5) + 'px 0; font-size: ' + scaledMetaFont + 'px; }');
    doc.write('.meta-item { }');
    doc.write('.date-block { font-size: ' + scaledDateFont + 'px; line-height: 1.2; display: grid; grid-template-columns: auto auto; column-gap: ' + scaledColumnGap + 'px; row-gap: ' + scaledRowGap + 'px; justify-content: end; text-align: left; }');
    doc.write('.date-label { font-weight: 600; }');
    doc.write('.info-lines { font-size: ' + scaledInfoFont + 'px; margin: ' + (scaledMargin * 0.5) + 'px 0 ' + (scaledMargin * 0.6) + 'px 0; font-weight: 600; page-break-inside: avoid; }');
    doc.write('.info-row { display: grid; grid-template-columns: 1fr 1fr; column-gap: ' + scaledGap + 'px; }');
    doc.write('.info-item { text-align: left; }');
    doc.write('.footer-line { margin-top: ' + (scaledMargin * 0.5) + 'px; text-align: left; font-size: ' + scaledFooterFont + 'px; color: #333; }');
    doc.write('</style>');
    doc.write('</head><body>');

    doc.write('<div class="container">');
    doc.write('<h1>|| SHREE GANESHAY NAMH ||</h1>');
    doc.write('<div class="header">');
    doc.write('<img src="' + logo + '" class="logo" alt="Logo">');
    doc.write('<div class="date-block">');
    doc.write('<div class="date-label">Date:</div><div>' + formatDate(receiptData.timestamp) + '</div>');
    doc.write('<div class="date-label">Time:</div><div>' + new Date(receiptData.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) + '</div>');
    doc.write('<div class="date-label">Name:</div><div>Manoj Shinde</div>');
    doc.write('<div class="date-label">Mobile:</div><div>8551881892</div>');
    doc.write('</div>');
    doc.write('</div>');

    doc.write('<div class="meta-row">');
    doc.write('<div class="meta-item"><strong>Receipt No:</strong> '+'<span style="font-weight: bold; font-size: ' + scaledReceiptNoFont + 'px; color: #000;">' + (receiptData.receiptNo || 'N/A') + '</span><br><strong>Name:</strong> <span style="font-weight: bold; font-size: ' + scaledNameFont + 'px; color: #000;">' + (receiptData.name || 'N/A') + '</span></div>');
    doc.write('</div>');

    doc.write('<table class="table">');
    doc.write('<thead>');
    doc.write('<tr><th>Transaction Type</th><th>Advance Type</th><th>Amount</th></tr>');
    doc.write('</thead>');
    doc.write('<tbody>');
    doc.write('<tr>');
    doc.write('<td>' + (receiptData.type || 'N/A') + '</td>');
    doc.write('<td>' + (receiptData.advanceType || 'N/A') + '</td>');
    doc.write('<td>' + (receiptData.advanceType === 'cash' ? `₹${receiptData.cashAmount}` : `${receiptData.pureWeight} grams`) + '</td>');
    doc.write('</tr>');
    doc.write('</tbody>');
    doc.write('</table>');

    doc.write('<div class="totals">');
    doc.write('<div class="total-row">');
    doc.write('<span>Advance Amount:</span>');
    doc.write('<span>' + (receiptData.advanceType === 'cash' ? `₹${receiptData.cashAmount}` : `${receiptData.pureWeight} grams`) + '</span>');
    doc.write('</div>');
    doc.write('</div>');

    doc.write('<div class="info-lines">');
    doc.write('<div class="info-row">');
    doc.write('<div class="info-item">* हमारे यहाँ लंगड़ी एक्सचेंज करके मिलता है</div>');
    doc.write('<div class="info-item">* RSBL गोल्ड कॉइन मिलता है</div>');
    doc.write('</div>');
    doc.write('<div class="info-row">');
    doc.write('<div class="info-item">* AQUA REFINE 995 और 999 मिलता है</div>');
    doc.write('<div class="info-item">* गोल्ड और सिल्वर मेल्ट किया जाता है</div>');
    doc.write('</div>');
    doc.write('<div class="footer-line">powered by Riddhi Infotech - 7499391121</div>');
    doc.write('</div>');

    doc.write('</div>');
    doc.write('</body></html>');
    doc.close();

    printWindow.onload = function() {
      printWindow.contentWindow.focus();
      printWindow.contentWindow.print();
      document.body.removeChild(printWindow);
    };
  };

  const handleCloseReceipt = () => {
    setShowReceiptModal(false);
  };

  return (
    <div className="container mt-5">
      <h1>Advance Page</h1>

      {/* Receipt Modal */}
      {receiptData && showReceiptModal && (
        <div className="modal receipt-modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Receipt</h5>
            <button 
            type="button" 
            className="btn-close" 
            onClick={handleCloseReceipt}
            />
          </div>
          <div className="modal-body">
            <div className="receipt-content text-center">
            {/* Add Logo and Company Name */}
            <div className="mb-4">
              <img 
              src={logo} 
              alt="Company Logo" 
              style={{ width: '100px', height: 'auto' }}
              className="mb-2"
              />
              <h4 className="company-name">Speed Touch Gold</h4>
              <p className="company-address">
              123 Main Street, City<br />
              Phone: (123) 456-7890
              </p>
              <hr />
            </div>
            
              {/* Receipt Details */}
              <div className="text-start">
              <p><strong>Date:</strong> {formatDate(receiptData.timestamp)}</p>
              <p><strong>Client Name:</strong> {receiptData.name}</p>
              <p><strong>Transaction Type:</strong> {receiptData.type} {receiptData.advanceType} Advance</p>
              {receiptData.advanceType === 'cash' ? (
                <p><strong>Amount:</strong> Rs. {receiptData.cashAmount}</p>
              ) : (
                <p><strong>Pure Weight:</strong> {receiptData.pureWeight} grams</p>
              )}
              </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-top"></div>
              <p className="mb-0">Thank you for your business!</p>
              <small className="text-muted">This is a computer generated receipt</small>
            </div>
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="btn btn-secondary"
              onClick={handleCloseReceipt}
            >
              Close
            </button>
            <button 
              className="btn btn-primary"
              onClick={handlePrintReceipt}
              disabled={!receiptData}
            >
              {console.log('Receipt Data:', receiptData)}
              Print Receipt
            </button>
          </div>
          </div>
        </div>
       
      )}
    
      {/* Success Popup */}

    {showSuccessPopup && message && (
      <div 
        className="alert alert-success alert-dismissible fade show" 
        role="alert"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1050,
          minWidth: '300px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          animation: 'slideIn 0.5s ease-out'
        }}
      >
        <strong>Success!</strong> {message}
        <button 
          type="button" 
          className="btn-close" 
          onClick={() => setShowSuccessPopup(false)}
        />
      </div>
    )}

    {/* Error Alert */}
    {error && (
      <div className="alert alert-danger alert-dismissible fade show" role="alert">
        {error}
        <button 
          type="button" 
          className="btn-close" 
          onClick={() => setError(null)}
        />
      </div>
    )}
    
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">Advance Type</label>
        <div className="btn-group w-100">
          <button
            type="button"
            className={`btn ${formData.advanceType === 'cash' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ advanceType: 'cash' })}
          >
            Cash Advance
          </button>
          <button
            type="button"
            className={`btn ${formData.advanceType === 'pureWeight' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ advanceType: 'pureWeight' })}
          >
            Pure Weight Advance
          </button>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">Advance Direction</label>
        <div className="btn-group w-100">
          <button
            type="button"
            className={`btn ${formData.type === 'taken' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ type: 'taken' })}
          >
            taken
          </button>
          <button
            type="button"
            className={`btn ${formData.type === 'given' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ type: 'given' })}
          >
            given
          </button>
        </div>
      </div>

      <div className="mb-3 position-relative">
        <label htmlFor="name" className="form-label">Client Name</label>
        <input
          type="text"
          id="name"
          name="name"
          className="form-control"
          value={searchTerm}
          onChange={handleChange}
          required
          autoComplete="off"
        />
        {searchTerm && filteredClients.length > 0 && (
          <ul className="list-group position-absolute w-100" style={{ zIndex: 1000 }}>
            {filteredClients.map((client, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-action"
                onClick={() => handleClientSelect(client.name)}
                style={{ cursor: 'pointer' }}
              >
                {client.name}
              </li>
            ))}
          </ul>
        )}
        {clientNotFound && searchTerm && (
          <div className="text-danger mt-1">Client not found</div>
        )}
      </div>

      {formData.advanceType === 'cash' ? (
        <div className="mb-3">
          <label htmlFor="cashAmount" className="form-label">Cash Amount</label>
          <input
            type="number"
            id="cashAmount"
            name="cashAmount"
            className="form-control"
            value={formData.cashAmount}
            onChange={handleChange}
            required
            min="0"
            step="0.01"
          />
        </div>
      ) : (
        <div className="mb-3">
          <label htmlFor="pureWeight" className="form-label">Pure Weight</label>
          <input
              type="number"
              id="pureWeight"
              name="pureWeight"
              className="form-control"
              value={formData.pureWeight}
              onChange={handleChange}
              required
              min="0"
              step="0.001"
            />
            </div>
          )}

          <button type="submit" className="btn btn-primary">Create Advance</button>
          </form>

          <style jsx>{`
            @media print {
              body * {
                visibility: hidden;
              }
              .receipt-modal, 
              .receipt-modal * {
                visibility: visible;
              }
              .receipt-modal {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              .modal-dialog {
                max-width: 100%;
                width: 100%;
              }
              .modal-content {
                border: none;
                box-shadow: none;
              }
            }
            @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
            }
            .modal-backdrop {
            display: none;
            }
          `}</style>
        </div>
        );
      };

      export default Advance;
